import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import type { User } from '../types';
import {
  TOKEN_KEY,
  isTokenExpiringSoon,
  refreshToken,
  getCurrentUser,
  passwordLogin,
  logout as apiLogout,
} from '../services/authService';

const INACTIVITY_MS  = 10 * 60 * 1000; // 10 min — auto-logout on idle
const REFRESH_BUFFER = 5  * 60 * 1000; // schedule proactive refresh 5 min before expiry

// ── BroadcastChannel for multi-tab auth sync ──────────────────────────────────

const TAB_ID = Math.random().toString(36).slice(2);
let _authBC: BroadcastChannel | null = null;

function getAuthBC(): BroadcastChannel | null {
  if (_authBC) return _authBC;
  if (typeof BroadcastChannel === 'undefined') return null;

  _authBC = new BroadcastChannel('permus_auth');
  _authBC.onmessage = (ev: MessageEvent) => {
    const msg = ev.data;
    if (!msg || msg.source === TAB_ID) return;
    if (msg.type === 'LOGOUT') {
      window.__AUTH_TOKEN__ = null;
      sessionStorage.removeItem(TOKEN_KEY);
      window.location.replace('/login');
    } else if (msg.type === 'TOKEN_UPDATED') {
      window.__AUTH_TOKEN__ = msg.token;
      sessionStorage.setItem(TOKEN_KEY, msg.token);
    }
  };
  return _authBC;
}

function bcBroadcast(msg: Record<string, unknown>): void {
  getAuthBC()?.postMessage({ ...msg, source: TAB_ID });
}

// ── Token expiry parser ───────────────────────────────────────────────────────

function parseTokenExpiry(token: string): number | null {
  try {
    const b64     = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth() {
  const [user,    setUser]    = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { instance } = useMsal();

  const inactivityRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doRefreshRef    = useRef<() => Promise<void>>();
  // Wall-clock timestamp of the last real activity — read on visibilitychange
  // (see below) so a background/throttled tab's setTimeout being delayed
  // doesn't let an idle session keep appearing "logged in" once you tab back.
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    if (refreshRef.current)    clearTimeout(refreshRef.current);
  }, []);

  const logout = useCallback(async () => {
    clearTimers();
    bcBroadcast({ type: 'LOGOUT' });
    try { await apiLogout(); } catch { /* best-effort */ }
    window.__AUTH_TOKEN__ = null;
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
    // Without this, MSAL's own cache keeps useIsAuthenticated() true after we've
    // cleared our app session — PrivateLayout then shows a permanent spinner
    // instead of redirecting to /login (it only navigates there once BOTH our
    // user and MSAL's isAuthenticated are false). Mirrors the manual-logout
    // button's instance.clearCache() in TopBar.tsx.
    instance.clearCache().catch(() => {});
  }, [clearTimers, instance]);

  const resetInactivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(logout, INACTIVITY_MS);
  }, [logout]);

  const scheduleRefresh = useCallback((token: string) => {
    if (refreshRef.current) clearTimeout(refreshRef.current);
    const exp = parseTokenExpiry(token);
    if (!exp) return;
    const delay = Math.max(60_000, exp - Date.now() - REFRESH_BUFFER);
    refreshRef.current = setTimeout(() => doRefreshRef.current?.(), delay);
  }, []);

  const doRefresh = useCallback(async () => {
    try {
      const res = await refreshToken();
      window.__AUTH_TOKEN__ = res.token;
      sessionStorage.setItem(TOKEN_KEY, res.token);
      setUser(res.user);
      scheduleRefresh(res.token);
      // Deliberately NOT calling resetInactivity() here — this fires on a background
      // timer near token expiry regardless of user activity, so it must not extend a
      // session that's genuinely idle. Only real user interaction should reset the clock.
      bcBroadcast({ type: 'TOKEN_UPDATED', token: res.token });
    } catch {
      // The refresh token is genuinely gone (expired/revoked) — this is a real,
      // permanent logout, not a transient blip, so go through the full logout
      // path (revokes the cookie server-side, broadcasts to other tabs, and
      // clears MSAL's cache) rather than only clearing local state.
      logout();
    }
  }, [scheduleRefresh, logout]);

  useEffect(() => { doRefreshRef.current = doRefresh; }, [doRefresh]);

  const setSession = useCallback((token: string, userData: User) => {
    window.__AUTH_TOKEN__ = token;
    sessionStorage.setItem(TOKEN_KEY, token);
    setUser(userData);
    scheduleRefresh(token);
    resetInactivity();
  }, [scheduleRefresh, resetInactivity]);

  const loginWithPassword = useCallback(async (identifier: string, password: string) => {
    const data = await passwordLogin(identifier, password);
    setSession(data.token, data.user);
    return data;
  }, [setSession]);

  const getToken = useCallback((): string | null => window.__AUTH_TOKEN__ ?? null, []);

  // ── Session restore on mount ────────────────────────────────────────────────
  // Priority:
  //   1. Token already in memory (set by AuthCallback → navigate, same React session)
  //   2. Token in sessionStorage, expiring soon (page reload / same-tab
  //      renavigation — sessionStorage survives those) → refresh via the
  //      httpOnly cookie to keep THAT open session going.
  //   3. No token at all in this tab → deliberately do NOT fall back to the
  //      httpOnly-cookie refresh here, even if that cookie is still valid.
  //
  // This is what makes a brand-new tab/window or a pasted/bookmarked deep link
  // (e.g. https://uat.servicedesk.permus.io/workspace) always land on /login —
  // sessionStorage is empty there — while an actual in-page refresh (F5) on a
  // page you're already viewing keeps you on it, since sessionStorage survives
  // a reload. Only a genuinely open tab's session silently resumes; a fresh
  // navigation never does, regardless of how much longer the refresh cookie
  // has left to live.
  //
  // Path 1 calls GET /auth/me (fast, no rate-limit cost) instead of the more
  // expensive POST /auth/refresh, which is reserved for path 2.
  useEffect(() => {
    getAuthBC();
    (async () => {
      try {
        const stored = window.__AUTH_TOKEN__ || sessionStorage.getItem(TOKEN_KEY);
        // On public routes (/login, /auth/callback) an anonymous visit has no
        // token; a POST /auth/refresh would just 401 and could trigger the
        // interceptor's reload-to-/login. AuthCallback runs its own auth. So
        // when there's no usable token here, don't attempt a refresh.
        const path = window.location.pathname;
        const onPublicRoute = path === '/login' || path === '/auth/callback';
        if (stored && !isTokenExpiringSoon(stored)) {
          // Valid token already available — no POST /auth/refresh needed
          window.__AUTH_TOKEN__ = stored; // ensure in-memory copy is set
          const userData = await getCurrentUser(); // lightweight GET /auth/me
          setUser(userData);
          scheduleRefresh(stored);
          resetInactivity();
        } else if (stored && !onPublicRoute) {
          // A token exists for this tab but is expiring soon — same-tab
          // reload/renavigation. Keep it alive via the httpOnly cookie.
          const res = await refreshToken();
          setSession(res.token, res.user);
        }
        // else: no token in this tab at all — a fresh navigation. `user` stays
        // null here and PrivateLayout (App.tsx) redirects to /login.
      } catch {
        // No active session — that's fine; user stays null → login redirect
      } finally {
        setLoading(false);
      }
    })();
    return clearTimers;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset inactivity timer on user activity
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach(e => window.addEventListener(e, resetInactivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetInactivity));
  }, [user, resetInactivity]);

  // Browsers throttle (or fully suspend) setTimeout in a backgrounded/hidden tab,
  // so the inactivity timer above can fire late — or not until the tab is torn
  // down — leaving a genuinely-idle session looking "logged in" for a long time
  // after the accepted idle window has passed. Checking the wall-clock gap the
  // moment the tab becomes visible again catches that immediately, rather than
  // waiting on a timer that may have been asleep the whole time.
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current >= INACTIVITY_MS) logout();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, logout]);

  return { user, loading, logout, getToken, setSession, loginWithPassword };
}
